const API_URL = 'https://apienglish.nguyenluan.vn/api';

// --- Token and User Management ---
const getToken = () => localStorage.getItem('token');
const setToken = (token) => localStorage.setItem('token', token);
const removeToken = () => localStorage.removeItem('token');
const getCurrentUser = () => JSON.parse(localStorage.getItem('user'));
const setCurrentUser = (user) => localStorage.setItem('user', JSON.stringify(user));
const removeCurrentUser = () => localStorage.removeItem('user');

// --- Centralized API Fetch for Authenticated Requests ---
const authFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
        removeToken();
        removeCurrentUser();
        window.location.reload();
        throw new Error("Chưa đăng nhập, đang chuyển hướng...");
    }

    const headers = { ...options.headers };
    headers['Authorization'] = `Bearer ${token}`;
    if (options.body) {
        headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
        removeToken();
        removeCurrentUser();
        window.location.reload();
        throw new Error("Phiên đăng nhập hết hạn, đang chuyển hướng...");
    }

    return response;
};

// --- Auth API Calls ---
export const registerUser = async (username, password, licenseKey) => {
    try {
        const response = await fetch(`${API_URL}/users/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password, licenseKey })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Đăng ký thất bại');
        }
        return { success: true, data };
    } catch (error) {
        console.error('Lỗi khi đăng ký:', error);
        return { success: false, error: error.message };
    }
};

export const loginUser = async (username, password) => {
    try {
        const response = await fetch(`${API_URL}/users/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Đăng nhập thất bại');
        
        if (data.token && data.user) {
            setToken(data.token);
            // Đảm bảo lưu đầy đủ thông tin user bao gồm role
            setCurrentUser({
                id: data.user.id,
                username: data.user.username,
                email: data.user.email,
                role: data.user.role || 'user' // Mặc định là user nếu không có role
            });
            return { success: true, data };
        } else if (data.data && data.data.token) { // Fallback for nested data
            setToken(data.data.token);
            setCurrentUser({
                id: data.data.user.id,
                username: data.data.user.username,
                email: data.data.user.email,
                role: data.data.user.role || 'user'
            });
            return { success: true, data: data.data };
        }
        throw new Error('Token hoặc thông tin người dùng không hợp lệ');

    } catch (error) {
        console.error('Lỗi khi đăng nhập:', error);
        return { success: false, error: error.message };
    }
};

export const logoutUser = () => {
    removeToken();
    removeCurrentUser();
    // To ensure UI updates, we can also force a reload here
    window.location.reload();
    return { success: true };
};

export const isAuthenticated = () => {
    return getToken() !== null;
};

// --- Refactored Lesson API Calls ---
export const fetchLessons = async () => {
    try {
        const response = await authFetch(`${API_URL}/lessons`);
        if (!response.ok) {
            throw new Error('Không thể tải bài học.');
        }
        const lessons = await response.json();
        return lessons.map(lesson => ({ ...lesson, phrases: lesson.phrases || [] }));
    } catch (error) {
        // authFetch will throw an error on 401, which will be caught here.
        // We can log it, but the redirection is already handled.
        console.error('Error fetching lessons:', error.message);
        return [];
    }
};

export const createLesson = async (title) => {
    const response = await authFetch(`${API_URL}/lessons`, {
        method: 'POST',
        body: JSON.stringify({ title })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tạo bài học.');
    }
    return await response.json();
};

export const deleteLesson = async (id) => {
    const response = await authFetch(`${API_URL}/lessons/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) throw new Error('Không thể xóa bài học.');
    return true;
};

// --- Refactored Phrase API Calls ---
export const addPhrase = async (lessonId, combinedText) => {
    const response = await authFetch(`${API_URL}/phrases/combined`, {
        method: 'POST',
        body: JSON.stringify({ lessonId: lessonId, combinedText: combinedText })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể thêm cụm từ.');
    }
    return await response.json();
};

export const deletePhrase = async (id) => {
    const response = await authFetch(`${API_URL}/phrases/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) throw new Error('Không thể xóa cụm từ.');
    return true;
};

// --- Vocabulary API Calls ---
export const generateVocabularyBatch = async (topic, difficulty = 'intermediate', count = 10) => {
    const response = await authFetch(`${API_URL}/vocabulary/generate-batch`, {
        method: 'POST',
        body: JSON.stringify({ topic, difficulty, count })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tạo từ vựng hàng loạt.');
    }
    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
        return result.data;
    } else if (Array.isArray(result)) {
        return result;
    } else {
        console.warn('Unexpected batch vocabulary response format:', result);
        return [];
    }
};

export const generateVocabulary = async (topic, difficulty = 'intermediate') => {
    const response = await authFetch(`${API_URL}/vocabulary/generate`, {
        method: 'POST',
        body: JSON.stringify({ topic, difficulty })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tạo từ vựng.');
    }
    return await response.json();
};

export const getDueForReview = async (limit = 20) => {
    const response = await authFetch(`${API_URL}/vocabulary/due-for-review?limit=${limit}`);
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tải từ vựng cần ôn tập.');
    }
    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
        return result.data;
    } else if (Array.isArray(result)) {
        return result;
    } else {
        console.warn('Unexpected review response format:', result);
        return [];
    }
};

export const updateVocabularyAfterReview = async (vocabularyId, quality) => {
    const response = await authFetch(`${API_URL}/vocabulary/review/${vocabularyId}`, {
        method: 'PUT',
        body: JSON.stringify({ quality })
    });

    if (!response.ok) {
        let errorMessage = `Lỗi Server: ${response.status} ${response.statusText}`;
        const errorBody = await response.text(); // Read body once
        try {
            const err = JSON.parse(errorBody);
            errorMessage = err.error || err.message || errorMessage;
        } catch (e) {
            console.error("Server returned a non-JSON error response:", errorBody);
        }
        throw new Error(errorMessage);
    }

    return await response.json();
};

export const getVocabularyStats = async () => {
    const response = await authFetch(`${API_URL}/vocabulary/stats`);
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tải thống kê.');
    }
    return await response.json();
};

export const getVocabularyByTopic = async (topic, limit = 50) => {
    const response = await authFetch(`${API_URL}/vocabulary/topic/${encodeURIComponent(topic)}?limit=${limit}`);
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tải từ vựng theo chủ đề.');
    }
    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
        return result.data;
    } else if (Array.isArray(result)) {
        return result;
    } else {
        console.warn('Unexpected topic vocabulary response format:', result);
        return [];
    }
};

export const searchVocabulary = async (searchTerm) => {
    const response = await authFetch(`${API_URL}/vocabulary/search?q=${encodeURIComponent(searchTerm)}`);
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tìm kiếm từ vựng.');
    }
    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
        return result.data;
    } else if (Array.isArray(result)) {
        return result;
    } else {
        console.warn('Unexpected search response format:', result);
        return [];
    }
};

export const getAllUserVocabulary = async () => {
    const response = await authFetch(`${API_URL}/vocabulary/my-vocabulary`);
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể tải từ vựng của bạn.');
    }
    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
        return result.data; // Return just the array
    } else if (Array.isArray(result)) {
        return result; // Direct array response
    } else {
        console.warn('Unexpected vocabulary response format:', result);
        return [];
    }
};

export const addCustomVocabulary = async (vocabularyData) => {
    const response = await authFetch(`${API_URL}/vocabulary/custom`, {
        method: 'POST',
        body: JSON.stringify(vocabularyData)
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể thêm từ vựng.');
    }
    return await response.json();
};

export const deleteVocabulary = async (vocabularyId) => {
    const response = await authFetch(`${API_URL}/vocabulary/${vocabularyId}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        let errorMessage = 'Lỗi server';
        try {
            const err = await response.json();
            errorMessage = err.error || err.message || 'Không thể xóa từ vựng.';
            console.error('🗑️ Delete error response:', err);
        } catch (parseError) {
            console.error('🗑️ Could not parse error response:', parseError);
        }
        throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
};

// --- Main Export ---
const dataService = {
    registerUser,
    loginUser,
    logoutUser,
    isAuthenticated,
    getCurrentUser,
    fetchLessons,
    createLesson,
    deleteLesson,
    addPhrase,
    deletePhrase,
    // Vocabulary functions
    generateVocabularyBatch,
    generateVocabulary,
    getDueForReview,
    updateVocabularyAfterReview,
    getVocabularyStats,
    getVocabularyByTopic,
    searchVocabulary,
    getAllUserVocabulary,
    addCustomVocabulary,
    deleteVocabulary,
};

export default dataService;